import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/toast";
import { RouteErrorBoundary } from "@/components/ui/route-error-boundary";
import { useAuthStore } from "@/stores/auth-store";

import { AuthLayout } from "@/layouts/AuthLayout";
import { AppLayout } from "@/layouts/AppLayout";
import { AdminLayout } from "@/layouts/AdminLayout";
import { DeveloperLayout } from "@/layouts/DeveloperLayout";

import LoginPage from "@/pages/auth/login";
import MfaPage from "@/pages/auth/mfa";
import TenantSelectPage from "@/pages/auth/tenant-select";
import PasskeyEnrollmentPage from "@/pages/auth/passkey-enrollment";
import SessionExpiredPage from "@/pages/auth/session-expired";
import { SessionEndedState } from "@/components/shell/session-ended-state";
import { configureSessionExpiry, currentReturnPath, signInPathFor } from "@/lib/session-expiry";
import InviteAcceptPage from "@/pages/auth/invite-accept";
import TermsPage from "@/pages/auth/terms";
import { configureTermsGate } from "@/lib/terms-gate";
import { MfaEnrolmentPrompt } from "@/components/shell/mfa-enrolment-prompt";

import HomePage from "@/pages/enterprise/home";
import WalletsPage from "@/pages/enterprise/wallets";
import WalletDetailPage from "@/pages/enterprise/wallet-detail";
import WalletNewPage from "@/pages/enterprise/wallet-new";
import TransfersPage from "@/pages/enterprise/transfers";
import TransferDetailPage from "@/pages/enterprise/transfer-detail";
import TransferNewPage from "@/pages/enterprise/transfer-new";
import ApprovalsPage from "@/pages/enterprise/approvals";
import ApprovalDetailPage from "@/pages/enterprise/approval-detail";
import ApprovalEscalatePage from "@/pages/enterprise/approval-escalate";
import DeveloperPage from "@/pages/enterprise/developer";
import TreasuryPage from "@/pages/enterprise/treasury";
import TreasuryStakingPage from "@/pages/enterprise/treasury-staking";
import TreasuryStakingNewPage from "@/pages/enterprise/treasury-staking-new";
import TreasuryLendingPage from "@/pages/enterprise/treasury-lending";
import TreasuryLiquidityPage from "@/pages/enterprise/treasury-liquidity";
import TreasuryRoutesPage from "@/pages/enterprise/treasury-routes";
import TreasuryOpportunitiesPage from "@/pages/enterprise/treasury-opportunities";
import GovernancePage from "@/pages/enterprise/governance";
import GovernanceNewPage from "@/pages/enterprise/governance-new";
import GovernanceDetailPage from "@/pages/enterprise/governance-detail";
import GovernanceEvaluationPage from "@/pages/enterprise/governance-evaluation";
import MonitoringPage from "@/pages/enterprise/monitoring";
import MonitoringAlertsPage from "@/pages/enterprise/monitoring-alerts";
import MonitoringChainPage from "@/pages/enterprise/monitoring-chain";
import MonitoringProviderPage from "@/pages/enterprise/monitoring-provider";
import MonitoringBroadcastPage from "@/pages/enterprise/monitoring-broadcast";
import MonitoringFreshnessPage from "@/pages/enterprise/monitoring-freshness";
import ReconciliationPage from "@/pages/enterprise/reconciliation";
import NotificationsPage from "@/pages/enterprise/notifications";
import HotWalletsDashboardPage from "@/pages/enterprise/hot-wallets/dashboard";
import HotWalletDepositsPage from "@/pages/enterprise/hot-wallets/deposits";
import HotWalletReconciliationPage from "@/pages/enterprise/hot-wallets/reconciliation";
import HotWalletConsolePage from "@/pages/enterprise/hot-wallets/console";
import InvoicesListPage from "@/pages/enterprise/invoicing/list";
import InvoiceCreatePage from "@/pages/enterprise/invoicing/create";
import InvoiceDetailPage from "@/pages/enterprise/invoicing/detail";
import InvoicingSettingsPage from "@/pages/enterprise/invoicing/settings";
import InvoiceResolutionsPage from "@/pages/enterprise/invoicing/resolutions";
import PayCheckoutPage from "@/pages/public/pay";
import { FeatureGate } from "@/components/data-display/feature-gate";
import ProviderMigrationConnectPage from "@/pages/enterprise/provider-migration/connect";
import ProviderMigrationDiscoveryPage from "@/pages/enterprise/provider-migration/discovery";
import ProviderMigrationImportReviewPage from "@/pages/enterprise/provider-migration/import-review";
import ProviderMigrationExportEvidencePage from "@/pages/enterprise/provider-migration/export-evidence";
import ProviderMigrationDispositionPage from "@/pages/enterprise/provider-migration/disposition";
import ReconciliationExceptionsPage from "@/pages/enterprise/reconciliation-exceptions";
import EvidencePage from "@/pages/enterprise/evidence";
import EvidenceDetailPage from "@/pages/enterprise/evidence-detail";
import EvidenceExportPage from "@/pages/enterprise/evidence-export";
import EvidencePacketPage from "@/pages/enterprise/evidence-packet";
import RecoveryPage from "@/pages/enterprise/recovery";
import SettingsPage from "@/pages/enterprise/settings";
import AccountPage from "@/pages/enterprise/account";
import DeveloperApiKeysPage from "@/pages/enterprise/developer-api-keys";
import DeveloperWebhooksPage from "@/pages/enterprise/developer-webhooks";
import ReconciliationLedgerComparisonPage from "@/pages/enterprise/reconciliation-ledger-comparison";
import ReconciliationRunHistoryPage from "@/pages/enterprise/reconciliation-run-history";
import RecoveryRequestPage from "@/pages/enterprise/recovery-request";
import RecoveryApprovalPage from "@/pages/enterprise/recovery-approval";
import RecoveryExecutionPage from "@/pages/enterprise/recovery-execution";
import RecoveryBackupPage from "@/pages/enterprise/recovery-backup";
import RecoveryAuditPage from "@/pages/enterprise/recovery-audit";
import TreasuryCalendarPage from "@/pages/enterprise/treasury-calendar";
import TreasuryIntentPage from "@/pages/enterprise/treasury-intent";
import TreasuryLendingBorrowPage from "@/pages/enterprise/treasury-lending-borrow";
import TreasuryLendingSupplyPage from "@/pages/enterprise/treasury-lending-supply";
import TreasuryLendingPositionPage from "@/pages/enterprise/treasury-lending-position";
import TreasuryLendingIncidentPage from "@/pages/enterprise/treasury-lending-incident";
import TreasuryLiquidityQuotePage from "@/pages/enterprise/treasury-liquidity-quote";
import TreasuryLiquidityIntentPage from "@/pages/enterprise/treasury-liquidity-intent";
import TreasuryLiquidityExecutionPage from "@/pages/enterprise/treasury-liquidity-execution";
import TreasuryLiquidityReadinessPage from "@/pages/enterprise/treasury-liquidity-readiness";
import TreasuryLiquidityIncidentPage from "@/pages/enterprise/treasury-liquidity-incident";
import TreasuryStakingRewardsPage from "@/pages/enterprise/treasury-staking-rewards";
import TreasuryStakingUnstakePage from "@/pages/enterprise/treasury-staking-unstake";
import TreasuryStakingValidatorPage from "@/pages/enterprise/treasury-staking-validator";

import DeploymentPage from "@/pages/admin/deployment";
import DeploymentDetailPage from "@/pages/admin/admin-deployment-detail";
import AdminDeploymentPage from "@/pages/admin/admin-deployment";
import UsersPage from "@/pages/admin/users";
import UserDetailPage from "@/pages/admin/user-detail";
import InviteUserPage from "@/pages/admin/invite";
import AdminAccessRequestsPage from "@/pages/admin/access-requests";
import AdminOrgPage from "@/pages/admin/org";
import AdminRolesPage from "@/pages/admin/roles";
import AdminGroupsPage from "@/pages/admin/groups";
import AdminServiceAccountsPage from "@/pages/admin/service-accounts";
import AdminAuditLogPage from "@/pages/admin/audit-log";
import AdminSsoPage from "@/pages/admin/sso";
import AdminAuthConfigPage from "@/pages/admin/auth-config";
import AdminAuthDiagnosticsPage from "@/pages/admin/auth-diagnostics";
import AdminServicesPage from "@/pages/admin/services";
import AdminConfigViewerPage from "@/pages/admin/config-viewer";
import AdminSupportBundlePage from "@/pages/admin/support-bundle";
import AdminTraceLookupPage from "@/pages/admin/trace-lookup";
import SignerDevicesPage from "@/pages/admin/signer-devices";

import RouteNotFoundPage from "@/pages/system/route-not-found";
import ErrorRoutePage from "@/pages/system/error";
import SignerGetStartedPage from "@/pages/signer/get-started";
import GetAppAndroidPage from "@/pages/signer/get-app-android";

const AUTH_PATHS = ["/login", "/mfa", "/tenant-select", "/passkey-enrollment", "/session-expired"];
// /error must render regardless of session state — it is where an auth
// redirect (including "no session ever existed") lands, so it cannot itself
// require a session or bounce to /login.
// /error — see above. /pay is the public stablecoin-invoice checkout: the
// payer has no Alloy account and no session, so RequireAuth must never bounce
// them to the sign-in screen (nor hold them at the loading spinner waiting for
// a session that will never exist). It renders outside AppLayout and behind
// the `invoicing` feature flag.
const PUBLIC_PATHS = ["/error", "/pay"];

// Paths that own their own auth-step handling and must never be redirected
// by RequireAuth, in either direction: /invite/accept is unauthenticated on
// first load (an invited user with no session yet), then authenticated
// mid-flow the instant Google sign-in returns (step flips to "app") while
// the page still needs to call the admin-invite accept endpoint before
// routing the user anywhere. Neither AUTH_PATHS branch below is correct for
// that page: the login-step branch would bounce it to /login before the
// invite/sign-in prompt ever renders, and the app-step branch (meant for
// /login et al. once already signed in) would bounce it to /wallets before
// acceptance completes. See ALL-4851.
const SELF_MANAGED_AUTH_PATHS = ["/invite/accept", "/get-app/android"];

function RequireAuth({ children }: { children: React.ReactNode }) {
  const step = useAuthStore((s) => s.step);
  const errorReason = useAuthStore((s) => s.errorReason);
  const initSession = useAuthStore((s) => s.initSession);
  const sessionEndedReturnTo = useAuthStore((s) => s.sessionEndedReturnTo);
  const sessionEndedAutoRedirect = useAuthStore((s) => s.sessionEndedAutoRedirect);
  const mfaSuggested = useAuthStore((s) => s.session?.mfaState === "ENROLMENT_SUGGESTED");
  const mfaPromptDismissed = useAuthStore((s) => s.mfaPromptDismissed);
  const location = useLocation();

  // ALL-5718 (IAM-134): once GET /auth/session has confirmed the session ended,
  // nothing of the failed page is rendered; the one session-ended state replaces it.
  if (step === "session-ended") {
    return <SessionEndedState returnTo={sessionEndedReturnTo} autoRedirect={sessionEndedAutoRedirect} />;
  }

  // ALL-5747 (specs/alloy-mfa S-3, S-13): while the session's Alloy second
  // factor is pending (enrolment or challenge), the 2FA page replaces every
  // route, public and self-managed ones included. Checked before the terms
  // step: the backend gates MFA first, then terms. After a passed step the
  // store re-runs initSession and the route asked for renders in place.
  if (step === "mfa") {
    return <MfaPage />;
  }

  // ALL-5722 (specs/terms-acceptance-gate S-10): until the current terms are
  // accepted, the terms page replaces every route, public and self-managed ones
  // included, so nothing else is rendered or called. After acceptance the store
  // re-runs initSession and the route the person asked for (e.g. /invite/accept)
  // renders in place.
  if (step === "terms") {
    return <TermsPage />;
  }

  if (PUBLIC_PATHS.includes(location.pathname)) {
    return <>{children}</>;
  }

  if (step === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <div className="spinner" aria-label="Checking session" />
      </div>
    );
  }

  // A genuine transport failure while bootstrapping session/org context
  // (network error or 5xx) — never masqueraded as "logged out" or rendered
  // with fixture-backed nav/org data. See stores/auth-store.ts.
  if (step === "error") {
    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg px-6 text-center"
      >
        <p className="text-sm font-semibold text-ink">We couldn&apos;t reach Alloy</p>
        <p className="max-w-sm text-xs text-muted">
          {errorReason === "experience_unreachable"
            ? "Your session is valid, but the organization service did not respond. Check your connection and try again."
            : "The session service did not respond. Check your connection and try again."}
        </p>
        <button
          type="button"
          onClick={() => {
            void initSession();
          }}
          className="rounded-md border border-line px-4 py-2 text-xs font-semibold text-ink hover:border-primary"
        >
          Retry
        </button>
      </div>
    );
  }

  if (SELF_MANAGED_AUTH_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
    return <>{children}</>;
  }

  if (step === "app" && AUTH_PATHS.includes(location.pathname)) {
    return <Navigate to="/wallets" replace />;
  }

  if (step === "login" && !AUTH_PATHS.includes(location.pathname)) {
    // ALL-5718: a signed-out deep link keeps its path through sign-in.
    return <Navigate to={signInPathFor(currentReturnPath(location))} replace />;
  }

  // ALL-5747: at organization level `enrol` the app is usable and the shell
  // offers enrolment above every page until it is set up or skipped.
  if (step === "app" && mfaSuggested && !mfaPromptDismissed) {
    return (
      <>
        <MfaEnrolmentPrompt />
        {children}
      </>
    );
  }

  return <>{children}</>;
}

export function App() {
  const initSession = useAuthStore((s) => s.initSession);

  useEffect(() => {
    initSession();
  }, [initSession]);

  // ALL-5718 (IAM-134): only an established session can end; the sign-in pages
  // and the initial load never raise the session-ended state. The terms page
  // (ALL-5722) runs on an established session, so it is armed too.
  useEffect(
    () =>
      configureSessionExpiry({
        isArmed: () => ["app", "terms"].includes(useAuthStore.getState().step),
        onEnded: (event) => useAuthStore.getState().sessionEnded(event),
      }),
    [],
  );

  // ALL-5722: a 403 AUTH_TERMS_ACCEPTANCE_REQUIRED mid-session (a new terms
  // version) re-checks the session once; the terms page then replaces the app.
  useEffect(
    () =>
      configureTermsGate({
        isArmed: () => useAuthStore.getState().step === "app",
        onRequired: () => useAuthStore.getState().initSession(),
      }),
    [],
  );

  return (
    <RequireAuth>
      <Toaster />
      <RouteErrorBoundary>
        <Routes>
          {/* Auth routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/tenant-select" element={<TenantSelectPage />} />
            <Route path="/passkey-enrollment" element={<PasskeyEnrollmentPage />} />
          </Route>
          <Route path="/session-expired" element={<SessionExpiredPage />} />
          {/* ALL-5747: the 2FA page normally renders in place of any route (step
              mfa); at /mfa outside that step it sends the person on. */}
          <Route path="/mfa" element={<MfaPage />} />
          {/* ALL-5722: where admin-portal and enterprise-wallet send a person whose
              call answered AUTH_TERMS_ACCEPTANCE_REQUIRED; returns to return_to. */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/invite/accept" element={<InviteAcceptPage />} />
          <Route path="/get-app/android" element={<GetAppAndroidPage />} />

          {/* Enterprise wallet routes */}
          <Route element={<AppLayout variant="enterprise" />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/signer/get-started" element={<SignerGetStartedPage />} />
            <Route path="/wallets" element={<WalletsPage />} />
            <Route path="/wallets/:id" element={<WalletDetailPage />} />
            <Route path="/wallets/new" element={<WalletNewPage />} />
            <Route path="/transfers" element={<TransfersPage />} />
            <Route path="/transfers/:id" element={<TransferDetailPage />} />
            <Route path="/transfers/new" element={<TransferNewPage />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/approvals/:id" element={<ApprovalDetailPage />} />
            <Route path="/approvals/:id/escalate" element={<ApprovalEscalatePage />} />
            <Route path="/treasury" element={<TreasuryPage />} />
            <Route path="/treasury/staking" element={<TreasuryStakingPage />} />
            <Route path="/treasury/staking/new" element={<TreasuryStakingNewPage />} />
            <Route path="/treasury/lending" element={<TreasuryLendingPage />} />
            <Route path="/treasury/liquidity" element={<TreasuryLiquidityPage />} />
            <Route path="/treasury/routes" element={<TreasuryRoutesPage />} />
            <Route path="/treasury/opportunities" element={<TreasuryOpportunitiesPage />} />
            <Route path="/governance" element={<GovernancePage />} />
            {/* Static segment before the dynamic one so `new` is the authoring
                page, never a policy id. */}
            <Route path="/governance/new" element={<GovernanceNewPage />} />
            <Route path="/governance/:id" element={<GovernanceDetailPage />} />
            <Route path="/governance/evaluation" element={<GovernanceEvaluationPage />} />
            <Route path="/monitoring" element={<MonitoringPage />} />
            <Route path="/monitoring/alerts" element={<MonitoringAlertsPage />} />
            <Route path="/monitoring/chain" element={<MonitoringChainPage />} />
            <Route path="/monitoring/provider" element={<MonitoringProviderPage />} />
            <Route path="/monitoring/broadcast" element={<MonitoringBroadcastPage />} />
            <Route path="/monitoring/freshness" element={<MonitoringFreshnessPage />} />
            <Route path="/reconciliation" element={<ReconciliationPage />} />
            {/* Hot-wallets routes are reachable by link and by typed address even while
                the flag is off, so the closed state routes to the wallets list rather
                than showing a 404 for a page the workspace will gain later. */}
            <Route path="/hot-wallets" element={<FeatureGate flag="hot_wallets" fallback={<Navigate to="/wallets" replace />}><HotWalletsDashboardPage /></FeatureGate>} />
            <Route path="/hot-wallets/:walletId" element={<FeatureGate flag="hot_wallets" fallback={<Navigate to="/wallets" replace />}><HotWalletsDashboardPage /></FeatureGate>} />
            <Route path="/hot-wallets/:walletId/deposits" element={<FeatureGate flag="hot_wallets" fallback={<Navigate to="/wallets" replace />}><HotWalletDepositsPage /></FeatureGate>} />
            <Route path="/hot-wallets/:walletId/reconciliation" element={<FeatureGate flag="hot_wallets" fallback={<Navigate to="/wallets" replace />}><HotWalletReconciliationPage /></FeatureGate>} />
            {/* Stablecoin invoicing (page specs invoices-list, invoice-create,
                invoice-detail, invoicing-settings, invoice-resolution-approval).
                The whole section ships dark behind the `invoicing` flag and each
                route redirects to the wallets list while it is off, the same way
                the hot-wallets routes do — these are reachable by a typed address
                or a stray link, and a 404 would be the wrong answer for a page
                the workspace will gain. */}
            <Route path="/stablecoins/invoices" element={<FeatureGate flag="invoicing" fallback={<Navigate to="/wallets" replace />}><InvoicesListPage /></FeatureGate>} />
            <Route path="/stablecoins/invoices/new" element={<FeatureGate flag="invoicing" fallback={<Navigate to="/wallets" replace />}><InvoiceCreatePage /></FeatureGate>} />
            <Route path="/stablecoins/invoices/view" element={<FeatureGate flag="invoicing" fallback={<Navigate to="/wallets" replace />}><InvoiceDetailPage /></FeatureGate>} />
            <Route path="/stablecoins/invoices/settings" element={<FeatureGate flag="invoicing" fallback={<Navigate to="/wallets" replace />}><InvoicingSettingsPage /></FeatureGate>} />
            <Route path="/stablecoins/invoices/resolutions" element={<FeatureGate flag="invoicing" fallback={<Navigate to="/wallets" replace />}><InvoiceResolutionsPage /></FeatureGate>} />
            <Route path="/reconciliation/provider-migration/connect" element={<ProviderMigrationConnectPage />} />
            <Route path="/reconciliation/provider-migration/discovery" element={<ProviderMigrationDiscoveryPage />} />
            <Route path="/reconciliation/provider-migration/import-review" element={<ProviderMigrationImportReviewPage />} />
            <Route path="/evidence/provider-migration/export" element={<ProviderMigrationExportEvidencePage />} />
            <Route path="/reconciliation/provider-migration/disposition" element={<ProviderMigrationDispositionPage />} />
            <Route path="/reconciliation/exceptions" element={<ReconciliationExceptionsPage />} />
            <Route path="/evidence" element={<EvidencePage />} />
            <Route path="/evidence/:id" element={<EvidenceDetailPage />} />
            <Route path="/evidence/export" element={<EvidenceExportPage />} />
            <Route path="/evidence/packet" element={<EvidencePacketPage />} />
            <Route path="/recovery" element={<RecoveryPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/reconciliation/ledger-comparison" element={<ReconciliationLedgerComparisonPage />} />
            <Route path="/reconciliation/run-history" element={<ReconciliationRunHistoryPage />} />
            <Route path="/recovery/request" element={<RecoveryRequestPage />} />
            <Route path="/recovery/approval" element={<RecoveryApprovalPage />} />
            <Route path="/recovery/execution" element={<RecoveryExecutionPage />} />
            <Route path="/recovery/backup" element={<RecoveryBackupPage />} />
            <Route path="/recovery/audit" element={<RecoveryAuditPage />} />
            <Route path="/treasury/calendar" element={<TreasuryCalendarPage />} />
            <Route path="/treasury/intent" element={<TreasuryIntentPage />} />
            <Route path="/treasury/lending/borrow" element={<TreasuryLendingBorrowPage />} />
            <Route path="/treasury/lending/supply" element={<TreasuryLendingSupplyPage />} />
            <Route path="/treasury/lending/position" element={<TreasuryLendingPositionPage />} />
            <Route path="/treasury/lending/incident" element={<TreasuryLendingIncidentPage />} />
            <Route path="/treasury/liquidity/quote" element={<TreasuryLiquidityQuotePage />} />
            <Route path="/treasury/liquidity/intent" element={<TreasuryLiquidityIntentPage />} />
            <Route path="/treasury/liquidity/execution" element={<TreasuryLiquidityExecutionPage />} />
            <Route path="/treasury/liquidity/readiness" element={<TreasuryLiquidityReadinessPage />} />
            <Route path="/treasury/liquidity/incident" element={<TreasuryLiquidityIncidentPage />} />
            <Route path="/treasury/staking/rewards" element={<TreasuryStakingRewardsPage />} />
            <Route path="/treasury/staking/unstake" element={<TreasuryStakingUnstakePage />} />
            <Route path="/treasury/staking/validator" element={<TreasuryStakingValidatorPage />} />
          </Route>

          {/* Developer routes — its own app/sidebar section (matches ApplicationSwitcher's
              existing "developer" entry), not nested under Enterprise Wallet's nav. */}
          <Route element={<DeveloperLayout />}>
            <Route path="/developer" element={<DeveloperPage />} />
            <Route path="/developer/api-keys" element={<DeveloperApiKeysPage />} />
            <Route path="/developer/webhooks" element={<DeveloperWebhooksPage />} />
            <Route path="/developer/hot-wallets" element={<FeatureGate flag="hot_wallets" fallback={<Navigate to="/developer" replace />}><HotWalletConsolePage /></FeatureGate>} />
          </Route>

          {/* Admin routes */}
          <Route element={<AdminLayout />}>
            <Route path="/admin/deployment" element={<AdminDeploymentPage />} />
            <Route path="/admin/deployment/:id" element={<DeploymentDetailPage />} />
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/users/:id" element={<UserDetailPage />} />
            <Route path="/admin/invite" element={<InviteUserPage />} />
            <Route path="/admin/access-requests" element={<AdminAccessRequestsPage />} />
            <Route path="/admin/access-requests/:id" element={<AdminAccessRequestsPage />} />
            <Route path="/deployment" element={<DeploymentPage />} />
            <Route path="/admin/org" element={<AdminOrgPage />} />
            <Route path="/admin/roles" element={<AdminRolesPage />} />
            <Route path="/admin/groups" element={<AdminGroupsPage />} />
            <Route path="/admin/service-accounts" element={<AdminServiceAccountsPage />} />
            <Route path="/admin/audit-log" element={<AdminAuditLogPage />} />
            <Route path="/admin/sso" element={<AdminSsoPage />} />
            <Route path="/admin/auth-config" element={<AdminAuthConfigPage />} />
            <Route path="/admin/auth-diagnostics" element={<AdminAuthDiagnosticsPage />} />
            <Route path="/admin/services" element={<AdminServicesPage />} />
            <Route path="/admin/config-viewer" element={<AdminConfigViewerPage />} />
            <Route path="/admin/support-bundle" element={<AdminSupportBundlePage />} />
            <Route path="/admin/trace-lookup" element={<AdminTraceLookupPage />} />
            <Route path="/admin/signer-devices" element={<SignerDevicesPage />} />
          </Route>

          {/* Branded, internationalised error page (ALL-4857) — reached via a
              backend auth-redirect (?code=&ref=) or linked directly. Public:
              rendered outside AppLayout/AdminLayout/etc chrome and outside the
              session-based redirects above. */}
          <Route path="/error" element={<ErrorRoutePage />} />

          {/* Public stablecoin-invoice checkout (page spec invoice-pay-checkout).
              Outside every layout — the payer is anonymous, so there is no app
              chrome, no session bootstrap and no redirect to sign-in (see
              PUBLIC_PATHS above). Behind the same `invoicing` flag as the issuer
              console: while it is off, there is no live checkout to route to. */}
          <Route path="/pay" element={<FeatureGate flag="invoicing" fallback={<Navigate to="/wallets" replace />}><PayCheckoutPage /></FeatureGate>} />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/wallets" replace />} />
          <Route path="*" element={<RouteNotFoundPage />} />
        </Routes>
      </RouteErrorBoundary>
    </RequireAuth>
  );
}
